/*
 * Shared formatting utilities — single source of truth for the entire UI.
 *
 * Previously duplicated across DashboardView, DeviceDrawer, DiagnosticsPanel,
 * NodeSummaryPanel, and NetworkCanvas.
 *
 * Every amount climbs to the unit a person would use for it: bytes run up to
 * terabytes and petabytes, and a span of time runs up to days, weeks, months
 * and years. "4,012 GB" and "157 h" are numbers to work out, not to read.
 *
 * ! file utils/formatters.js
 * ---------------
 * \author: Tolga O. Atalay
 * \Affiliation: A2 Labs LLC / A12XT Inc.
 * \date: 2025
 */

const BYTE_UNITS = ["B", "KB", "MB", "GB", "TB", "PB"];

/* scaled picks the largest unit an amount reaches, so a value never sits in
   one unit past the next one's threshold. */
const scaled = (v, base) => {
  let i = 0;
  while (Math.abs(v) >= base && i < BYTE_UNITS.length - 1) {
    v /= base;
    i++;
  }
  return [v, i];
};

/**
 * Format a byte count into a human-readable string (B / KB / MB / GB / TB / PB).
 * Binary multiples, as the kernel counts them.
 * @param {number|null|undefined} b
 * @returns {string}
 */
export const fmtBytes = (b) => {
  if (b == null || !isFinite(b)) return "—";
  const [v, i] = scaled(b, 1024);
  if (i === 0) return `${Math.round(v)} B`;
  return `${v.toFixed(i < 3 ? 1 : 2)} ${BYTE_UNITS[i]}`;
};

/**
 * Format decimal bytes (1 GB = 10^9 B), as carriers bill them.
 * @param {number|null|undefined} b
 * @param {number} digits decimals below 100 of the unit
 * @returns {string}
 */
export const fmtBytesDecimal = (b, digits = 1) => {
  if (b == null || !isFinite(b)) return "—";
  const [v, i] = scaled(b, 1000);
  if (i === 0) return `${Math.round(v)} B`;
  return `${Math.abs(v) >= 100 ? v.toFixed(0) : v.toFixed(digits)} ${BYTE_UNITS[i]}`;
};

/**
 * Format a number with locale-aware thousand separators.
 * @param {number|null|undefined} n
 * @returns {string}
 */
export const fmtNum = (n) => (n != null ? Number(n).toLocaleString() : "—");

/**
 * Format a byte-rate as a human-readable per-second string.
 * @param {number|null|undefined} r
 * @returns {string}
 */
export const fmtRate = (r) => {
  if (r == null || r === 0 || !isFinite(r)) return "0 B/s";
  const [v, i] = scaled(r, 1024);
  if (i === 0) return `${v.toFixed(0)} B/s`;
  return `${v.toFixed(i < 3 ? 1 : 2)} ${BYTE_UNITS[i]}/s`;
};

const BIT_UNITS = {
  bps: ["bps", "kbps", "Mbps", "Gbps", "Tbps"],
  "bit/s": ["bit/s", "kbit/s", "Mbit/s", "Gbit/s", "Tbit/s"],
};

/**
 * Format a bit rate given in megabits per second, in the largest decimal unit
 * it reaches: "500 kbps", "12.3 Mbps", "2.4 Gbps". `style` "bit/s" writes
 * "Mbit/s" instead, as limits and budgets are worded. Zero and below are
 * left to the caller, which knows whether nothing means "0" or "not known".
 * @param {number|null|undefined} mbps
 * @param {{style?: "bps"|"bit/s"}} [opts]
 * @returns {string}
 */
export const fmtBitRate = (mbps, { style = "bps" } = {}) => {
  if (mbps == null || !isFinite(mbps)) return "—";
  const units = BIT_UNITS[style] || BIT_UNITS.bps;
  let v = mbps * 1e6;
  let i = 0;
  while (Math.abs(v) >= 1000 && i < units.length - 1) {
    v /= 1000;
    i++;
  }
  const digits = Math.abs(v) >= 100 ? 0 : Math.abs(v) >= 10 ? 1 : 2;
  return `${Number(v.toFixed(digits))} ${units[i]}`;
};

/* The spans of time a person counts in, largest first. A month is thirty
   days and a year 365: close enough for any figure this console shows, and
   honest about being a reading rather than a calendar. */
const SPANS = [
  { sec: 365 * 86400, one: "year", many: "years" },
  { sec: 30 * 86400, one: "month", many: "months" },
  { sec: 7 * 86400, one: "week", many: "weeks" },
  { sec: 86400, one: "day", many: "days" },
  { sec: 3600, one: "h", many: "h" },
  { sec: 60, one: "min", many: "min" },
  { sec: 1, one: "s", many: "s" },
];

const spanWords = (n, span) => `${n} ${n === 1 ? span.one : span.many}`;

/**
 * Format a span of seconds in the largest units it reaches: "42 s",
 * "12 min 5 s", "5 h 12 min", "6 days 13 h", "2 weeks 3 days",
 * "4 months 1 week", "1 year 2 months".
 * @param {number|null|undefined} seconds
 * @param {number} parts how many units to name, the largest first
 * @returns {string}
 */
export const fmtDuration = (seconds, parts = 2) => {
  if (seconds == null || !isFinite(seconds)) return "—";
  let left = Math.max(0, Math.round(seconds));
  if (left === 0) return "0 s";
  const out = [];
  for (const span of SPANS) {
    if (out.length >= parts) break;
    const n = Math.floor(left / span.sec);
    if (n > 0) {
      out.push(spanWords(n, span));
      left -= n * span.sec;
    } else if (out.length > 0) {
      break; // "2 weeks 0 days" is "2 weeks"; never skip to a smaller unit
    }
  }
  return out.join(" ");
};

const GO_UNITS = { h: 3600, m: 60, s: 1, ms: 1e-3, us: 1e-6, "µs": 1e-6, ns: 1e-9 };

/**
 * parseGoDuration reads the string Go's time.Duration prints ("157h37m53s",
 * "1m30.5s", "450ms") as seconds, or null when it is not one.
 * @param {string|null|undefined} s
 * @returns {number|null}
 */
export const parseGoDuration = (s) => {
  if (typeof s !== "string") return null;
  const text = s.trim();
  if (!/^-?(\d+(\.\d+)?(h|ms|us|µs|ns|m|s))+$/.test(text)) return null;
  let total = 0;
  for (const [, n, unit] of text.matchAll(/(\d+(?:\.\d+)?)(h|ms|us|µs|ns|m|s)/g)) {
    total += Number(n) * GO_UNITS[unit];
  }
  return text.startsWith("-") ? -total : total;
};

/**
 * fmtGoDuration formats a duration the appliance reports in Go's notation,
 * such as its uptime, in the units a person counts in. Anything else is shown
 * as it came.
 * @param {string|null|undefined} s
 * @returns {string}
 */
export const fmtGoDuration = (s) => {
  if (s == null || s === "") return "—";
  const sec = parseGoDuration(s);
  return sec == null ? String(s) : fmtDuration(sec);
};

/**
 * Turn a timestamp into a human-readable "time ago" string.
 * Handles ISO strings, Unix timestamps, and Date objects.
 * @param {string|number|Date|null|undefined} ts
 * @returns {string}
 */
export const ago = (ts) => {
  if (!ts) return "—";
  const d = new Date(ts);
  if (isNaN(d.getTime()) || d.getFullYear() < 2000) return "never";
  const sec = Math.floor((Date.now() - d.getTime()) / 1000);
  if (sec < 1) return "just now";
  // Past a day the hours stop mattering: "3 days ago", not "3 days 4 h ago".
  return `${fmtDuration(sec, sec < 86400 ? 2 : 1)} ago`;
};

/**
 * Truncate a string to `max` characters and append "…" if needed.
 * @param {string} str
 * @param {number} max
 * @returns {string}
 */
export const truncate = (str, max = 32) => {
  if (!str) return "—";
  return str.length > max ? str.slice(0, max - 1) + "…" : str;
};
